import { formatDateForApi } from '@/utils/datePresentation';
import { getOptionCode, toPositiveOptionId } from '@/utils/optionValues';
import { toAmount } from '@/utils/numberValue';

const buildTodayDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const addOneMonthPreservingDay = (date) => {
  const source = date instanceof Date ? date : buildTodayDate();
  const year = source.getFullYear();
  const month = source.getMonth();
  const day = source.getDate();

  const nextMonthStart = new Date(year, month + 1, 1);
  const nextMonthLastDay = new Date(year, month + 2, 0).getDate();
  nextMonthStart.setDate(Math.min(day, nextMonthLastDay));

  return nextMonthStart;
};

export const SALE_MULTI_CUSTOMER_MODAL_SECTIONS = [
  {
    key: 'general',
    title: 'Información General',
    description: 'Datos base del negocio, cliente y montos.',
    icon: 'fa-file-text-o',
  },
  {
    key: 'configuration',
    title: 'Gestión Operativa',
    description: 'Responsables, etiquetas y seguimiento.',
    icon: 'fa-cogs',
  },
  {
    key: 'details',
    title: 'Detalles',
    description: 'Alcance y comentarios del negocio.',
    icon: 'fa-align-left',
  },
  {
    key: 'quote',
    title: 'Cotización',
    description: 'Origen y referencias de cotización.',
    icon: 'fa-file-text-o',
  },
];

export const createInitialSaleMultiCustomerForm = () => ({
  saleDate: buildTodayDate(),
  deliveryDate: addOneMonthPreservingDay(buildTodayDate()),
  code: '',
  customer: null,
  customers: [null],
  saleName: '',
  service: null,
  area: null,
  tags: [],
  status: null,
  subtotal: 0,
  budgetGeneral: 0,
  agencyAmount: 0,
  mediaAmount: 0,
  currencyCode: 'MXN',
  exchangeRate: 1,
  manager: null,
  seller: null,
  quoteMode: 'url',
  quoteUrl: '',
  quoteFile: null,
  quoteType: null,
  quotePrincipal: null,
  quoteExtras: [],
  scope: '',
  comments: '',
});

export const createInitialSaleMultiCustomerOptions = () => ({
  services: [],
  statuses: [],
  areas: [],
  sellers: [],
  managers: [],
  quoteTypes: [],
  quoteCatalogs: [],
});

export const normalizeStatusCode = (option) => {
  return String(getOptionCode(option, { fallback: '' })).trim().toLowerCase();
};

export const isStatusOpen = (option) => {
  const rawValue = option?.meta?.is_open ?? option?.is_open;

  if (typeof rawValue === 'boolean') {
    return rawValue;
  }

  if (typeof rawValue === 'number') {
    return rawValue === 1;
  }

  const normalized = String(rawValue ?? '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true';
};

export const getDefaultStatusOption = (statusOptions) => {
  const options = (Array.isArray(statusOptions) ? statusOptions : [])
    .filter((option) => String(option?.value ?? '').trim() !== '');

  if (options.length === 0) {
    return null;
  }

  const preferredByCode = options.find((option) => normalizeStatusCode(option) === 'activo')
    || options.find((option) => normalizeStatusCode(option) === 'abierto');

  if (preferredByCode) {
    return preferredByCode;
  }

  return options.find((option) => isStatusOpen(option)) || options[0] || null;
};

export const applyDefaultStatus = (form, statusOptions) => {
  if (form?.status) {
    return;
  }

  const defaultStatus = getDefaultStatusOption(statusOptions);
  if (defaultStatus) {
    form.status = defaultStatus;
  }
};

export const syncAgencyAmount = (form, isAdvertisingEnabled) => {
  const subtotal = toAmount(form?.subtotal);

  if (!isAdvertisingEnabled) {
    form.agencyAmount = subtotal;
    return;
  }

  const mediaAmount = toAmount(form?.mediaAmount);
  const agencyAmount = subtotal - mediaAmount;

  form.agencyAmount = Number.isFinite(agencyAmount) ? agencyAmount : 0;
};

export const buildSaleMultiCustomerPayload = (form, { isAdvertisingEnabled = true } = {}) => {
  const subtotal = toAmount(form?.subtotal);
  const budgetGeneral = toAmount(form?.budgetGeneral);
  const agencyAmount = isAdvertisingEnabled ? toAmount(form?.agencyAmount) : subtotal;
  const mediaAmount = isAdvertisingEnabled ? toAmount(form?.mediaAmount) : 0;

  const customerIds = (Array.isArray(form?.customers) ? form.customers : [])
    .map((customer) => toPositiveOptionId(customer))
    .filter((id) => id > 0);

  const primaryCustomerId = customerIds[0] || toPositiveOptionId(form?.customer);

  const quotePrincipalValue = String(form?.quotePrincipal?.value || '').trim();
  const quoteExtras = (Array.isArray(form?.quoteExtras) ? form.quoteExtras : [])
    .map((item) => String(item?.value || '').trim())
    .filter(Boolean);

  return {
    company_id: primaryCustomerId,
    customer_id: primaryCustomerId,
    customer_ids: customerIds,
    customers: customerIds,
    code: String(form?.code || '').trim(),
    name: String(form?.saleName || '').trim(),
    product_id: toPositiveOptionId(form?.service),
    sale_date: formatDateForApi(form?.saleDate, { emptyValue: null, allowString: true }),
    delivery_date: formatDateForApi(form?.deliveryDate, { emptyValue: null, allowString: true }),
    area_id: toPositiveOptionId(form?.area),
    tags: (Array.isArray(form?.tags) ? form.tags : []).map((tag) => String(tag || '').trim()).filter(Boolean),
    status_sale_id: toPositiveOptionId(form?.status),
    subtotal,
    sale_price: subtotal,
    budget_general: budgetGeneral,
    sale_import: agencyAmount,
    agency_amount: agencyAmount,
    media_amount: mediaAmount,
    advertising_amount: mediaAmount,
    manager_id: toPositiveOptionId(form?.manager),
    seller_id: toPositiveOptionId(form?.seller),
    quote_type_id: toPositiveOptionId(form?.quoteType),
    quote_id: toPositiveOptionId(form?.quotePrincipal),
    quote_type: quotePrincipalValue,
    quote_mode: form?.quoteMode,
    quote_document: form?.quoteMode === 'url' ? String(form?.quoteUrl || '').trim() : '',
    quote_url: form?.quoteMode === 'url' ? String(form?.quoteUrl || '').trim() : '',
    quote_file: form?.quoteMode === 'file' ? form?.quoteFile : null,
    quote_is_file: form?.quoteMode === 'file' ? 1 : 0,
    quote_principal: quotePrincipalValue,
    quote_extras: quoteExtras,
    scope: String(form?.scope || '').trim(),
    comments: String(form?.comments || '').trim(),
  };
};
